// Brief mutes happen when a device switches profiles; only a sustained one means the take is losing audio
const SUSTAINED_MUTE_MS = 2000;

// A mic that goes away mid-take (unplugged, Bluetooth dropped, permission revoked) ends its track and the browser stops the recorder on its own; onEnded lets the caller keep the audio captured so far and say why it ended. A mic that merely stops delivering frames fires mute instead; a sustained one means the take is silently losing audio, worth reporting while there is still time to fix the mic. Returns a cleanup that clears the timer and detaches the listeners.
export const watchMicrophoneTrack = (
  audioTrack: MediaStreamTrack,
  {
    onEnded,
    onSustainedMute,
  }: {
    onEnded: () => void;
    onSustainedMute: () => void;
  }
): (() => void) => {
  let sustainedMuteTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearSustainedMute = () => {
    if (sustainedMuteTimeout !== null) {
      clearTimeout(sustainedMuteTimeout);
      sustainedMuteTimeout = null;
    }
  };

  const startSustainedMute = () => {
    clearSustainedMute();
    sustainedMuteTimeout = setTimeout(() => {
      sustainedMuteTimeout = null;
      onSustainedMute();
    }, SUSTAINED_MUTE_MS);
  };

  audioTrack.addEventListener("ended", onEnded);
  audioTrack.addEventListener("mute", startSustainedMute);
  audioTrack.addEventListener("unmute", clearSustainedMute);

  // A Bluetooth headset still switching to its mic profile hands over a track that is already muted, and no mute event ever fires for a state that was there from the start
  if (audioTrack.muted) {
    startSustainedMute();
  }

  return () => {
    clearSustainedMute();
    audioTrack.removeEventListener("ended", onEnded);
    audioTrack.removeEventListener("mute", startSustainedMute);
    audioTrack.removeEventListener("unmute", clearSustainedMute);
  };
};
